import {
  canonicalSmartCreationTargetIntentKey,
  type SmartCreationAdGroupDraftContentV2,
} from "../schemas/smart-creation";
import type {
  SmartCreationReviewTarget,
  SmartCreationReviewWorkspace,
} from "../schemas/smart-creation-review";
import { reviewTargetMissing, SmartCreationReviewError } from "./errors";
import { draftContentAdGroupFor } from "./graph";
import { productsKey, targetStableKey } from "./lineage";
import {
  reviewTargetIntent,
  strategyOwnsAdGroup,
  strategySupportsReviewIntent,
} from "./strategy-intent";

export function sameProducts(
  left: SmartCreationAdGroupDraftContentV2,
  right: SmartCreationAdGroupDraftContentV2,
): boolean {
  return JSON.stringify(productsKey(left)) === JSON.stringify(productsKey(right));
}

export function moveKeywordTargetToMatchType(
  workspace: SmartCreationReviewWorkspace,
  target: Extract<SmartCreationReviewTarget, { type: "keyword" }>,
  matchType: "broad" | "phrase" | "exact",
): void {
  const sourceReviewAdGroup = workspace.graph.adGroups.find(
    (candidate) => candidate.ref.lineageKey === target.adGroupLineageKey,
  );
  if (!sourceReviewAdGroup) throw reviewTargetMissing(target.ref);
  const sourceDraftContentAdGroup = draftContentAdGroupFor(
    workspace.draftContent,
    sourceReviewAdGroup.ref.itemKey,
  );
  if (sourceDraftContentAdGroup.targeting.type !== "keyword") {
    throw new SmartCreationReviewError(
      422,
      "smart_creation.review_target_invalid",
      "Keyword Target does not belong to a keyword Ad Group",
    );
  }
  if (
    !strategySupportsReviewIntent(
      target.strategyKind,
      { type: "keyword", keyword: target.keyword, matchType },
      workspace.draftContent.previewGeneratorVersion,
    )
  ) {
    throw new SmartCreationReviewError(
      422,
      "smart_creation.review_target_invalid",
      "Match Type is not supported by the Target Strategy",
    );
  }
  if (sourceDraftContentAdGroup.targeting.matchType === matchType) return;
  const sourceCampaign = workspace.graph.campaigns.find(
    (candidate) => candidate.ref.lineageKey === sourceReviewAdGroup.campaignLineageKey,
  );
  if (!sourceCampaign) throw reviewTargetMissing(target.ref);
  const generatedCampaignKeys = new Set(
    workspace.suggestedDraft.campaigns.map((campaign) => campaign.itemKey),
  );
  const sourceIsGenerated = generatedCampaignKeys.has(sourceCampaign.ref.itemKey);
  // Standard Research splits Broad and Phrase into separate generated Campaigns.
  // Those canonical lanes may exchange Targets, but a user copy is its own scope.
  const destinationDraftContentAdGroup = workspace.draftContent.campaigns
    .filter((campaign) =>
      sourceIsGenerated
        ? generatedCampaignKeys.has(campaign.itemKey)
        : campaign.itemKey === sourceCampaign.ref.itemKey,
    )
    .flatMap((campaign) => campaign.adGroups)
    .find(
      (candidate) =>
        candidate.targeting.type === "keyword" &&
        candidate.targeting.matchType === matchType &&
        strategyOwnsAdGroup(
          candidate,
          target.strategyKind,
          workspace.draftContent.previewGeneratorVersion,
        ) &&
        sameProducts(candidate, sourceDraftContentAdGroup),
    );
  const destinationReviewAdGroup = destinationDraftContentAdGroup
    ? workspace.graph.adGroups.find(
        (candidate) => candidate.ref.itemKey === destinationDraftContentAdGroup.itemKey,
      )
    : undefined;
  if (!destinationDraftContentAdGroup || !destinationReviewAdGroup) {
    throw new SmartCreationReviewError(
      422,
      "smart_creation.review_target_invalid",
      "Changing Match Type requires an existing Ad Group lane for the same products",
    );
  }
  if (
    workspace.draftContent.previewGeneratorVersion >= 3 &&
    workspace.graph.targets.some(
      (candidate) =>
        candidate.type === "keyword" &&
        candidate.adGroupLineageKey === destinationReviewAdGroup.ref.lineageKey &&
        canonicalSmartCreationTargetIntentKey(reviewTargetIntent(candidate)) ===
          canonicalSmartCreationTargetIntentKey({
            type: "keyword",
            keyword: target.keyword,
            matchType,
          }),
    )
  ) {
    throw new SmartCreationReviewError(
      422,
      "smart_creation.review_target_invalid",
      "This keyword already exists in the destination Match Type lane",
    );
  }
  if (sourceReviewAdGroup.targetLineageKeys.length === 1) {
    throw new SmartCreationReviewError(
      422,
      "smart_creation.review_target_invalid",
      "Changing Match Type would leave the source Ad Group without Targets",
    );
  }
  sourceReviewAdGroup.targetLineageKeys = sourceReviewAdGroup.targetLineageKeys.filter(
    (lineageKey) => lineageKey !== target.ref.lineageKey,
  );
  destinationReviewAdGroup.targetLineageKeys.push(target.ref.lineageKey);
  target.adGroupLineageKey = destinationReviewAdGroup.ref.lineageKey;
  const destinationCampaign = workspace.graph.campaigns.find(
    (candidate) => candidate.ref.lineageKey === destinationReviewAdGroup.campaignLineageKey,
  );
  const baseDestinationAdGroup = workspace.suggestedDraft.campaigns
    .find((campaign) => campaign.itemKey === destinationCampaign?.ref.itemKey)
    ?.adGroups.find(
      (candidate) =>
        candidate.targeting.type === "keyword" &&
        candidate.targeting.matchType === matchType &&
        strategyOwnsAdGroup(
          candidate,
          target.strategyKind,
          workspace.suggestedDraft.previewGeneratorVersion,
        ) &&
        sameProducts(candidate, destinationDraftContentAdGroup),
    );
  const baseTarget = baseDestinationAdGroup?.targeting.targets.find(
    (candidate) => candidate.sourceTargetId === target.sourceTargetId,
  );
  target.ref.itemKey =
    baseTarget?.itemKey ??
    targetStableKey({
      adGroup: destinationReviewAdGroup.ref.itemKey,
      lineageKey: target.ref.lineageKey,
    });
}
